import React, { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import backgroundImage from '../../../assets/img/gallery/3.jpg';
import { ROUTES } from '../../routes/paths';
import { Features } from '../../../assets/features/Features';
import { sendAppointmentEmails } from '../../../data/email/appointmentEmailService';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import SEO from '../seo/SEO';
import { seoData } from '../../../data/seoData';
import { loadRecaptchaScript } from '../../../utils/loadRecaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { theme } = useTheme();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: Features.contactPage.contactForm.fields.subject.options.appointment[language], // Default option
    timePreference: '',
    message: '',
    acceptTerms: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // reCAPTCHA widget state
  const recaptchaRef = useRef<HTMLDivElement>(null);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Initialize AOS animation library
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false
    });
  }, []);

  // Initialize reCAPTCHA widget
  // (Google reCAPTCHA sets cookies — only loaded after the terms checkbox is ticked.)
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || !formData.acceptTerms) return;
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;

    const init = async () => {
      await loadRecaptchaScript();
      if (cancelled) return;

      const tryRender = () => {
        if (!recaptchaRef.current || typeof (window as any).grecaptcha === 'undefined') return false;
        if (recaptchaRef.current.hasAttribute('data-recaptcha-rendered')) return true;
        try {
          const id = (window as any).grecaptcha.render(recaptchaRef.current, {
            sitekey: RECAPTCHA_SITE_KEY,
            theme: theme === 'dark' ? 'dark' : 'light',
          });
          recaptchaRef.current.setAttribute('data-recaptcha-rendered', 'true');
          setRecaptchaWidgetId(id);
          return true;
        } catch (err) {
          console.error('reCAPTCHA render error:', err);
          return false;
        }
      };

      if (tryRender()) return;
      intervalId = setInterval(() => {
        if (tryRender() && intervalId) clearInterval(intervalId);
      }, 150);
    };

    const initTimeout = setTimeout(init, 100);
    return () => {
      cancelled = true;
      clearTimeout(initTimeout);
      if (intervalId) clearInterval(intervalId);
    };
  }, [theme, formData.acceptTerms]);

  // Update default subject when language changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      subject: Features.contactPage.contactForm.fields.subject.options.appointment[language]
    }));
  }, [language]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate form
    if (!formData.acceptTerms) {
      setError(Features.contactPage.contactForm.messages.termsError[language]);
      return;
    }

    // Get reCAPTCHA token
    const token =
      typeof (window as any).grecaptcha !== 'undefined' && recaptchaWidgetId != null
        ? (window as any).grecaptcha.getResponse(recaptchaWidgetId)
        : '';

    if (!token) {
      setError(
        language === 'en'
          ? 'Please complete the reCAPTCHA verification.'
          : 'Por favor complete a verificação reCAPTCHA.'
      );
      return;
    }

    // Snapshot any state we still need after the optimistic switch
    const tokenSnapshot = token;
    const formSnapshot = formData;

    setLoading(true);
    setSuccess(true);

    // Reset form + reCAPTCHA immediately for snappy UX
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: Features.contactPage.contactForm.fields.subject.options.appointment[language],
      timePreference: '',
      message: '',
      acceptTerms: false
    });
    if ((window as any).grecaptcha && recaptchaWidgetId != null) {
      (window as any).grecaptcha.reset(recaptchaWidgetId);
    }

    // Fire-and-forget: don't block the UI on the email round-trip
    sendAppointmentEmails(
      {
        name: formSnapshot.name,
        email: formSnapshot.email,
        phone: formSnapshot.phone,
        preferredDate: formSnapshot.timePreference || 'A definir',
        specialist: 'Dr. Guilherme Galvão',
        subject: formSnapshot.subject,
        message: formSnapshot.message,
      },
      tokenSnapshot
    ).catch((err) => console.error('Background mail send failed:', err));
  };

  // Handle appointment button click
  const handleAppointmentClick = () => {
    navigate(ROUTES.HOME);
    setTimeout(() => {
      const appointmentSection = document.getElementById('appointment');
      if (appointmentSection) {
        appointmentSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Handle terms and conditions link click
  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(ROUTES.TERMOS);
  };

  return (
    <>
      <SEO 
        title={seoData.contact.title}
        description={seoData.contact.description}
        keywords={seoData.contact.keywords}
        canonical={seoData.contact.canonical}
      />
      {/* Hero Section */}
      <section 
        className="contact-hero d-flex align-items-center" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          position: 'relative'
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-white">
              <h1 
                className="display-4 fw-bold" 
                data-aos="fade-up" 
                style={{ 
                  color: '#ffffff', 
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)'
                }}
              >
                {Features.contactPage.hero.title[language]}
              </h1>
              <div className="divider mx-auto my-4" data-aos="fade-up" data-aos-delay="100" style={{
                width: '80px',
                height: '4px',
                background: 'var(--primary-color, #1977cc)',
                margin: '0 auto'
              }}></div>
              <p className="lead" data-aos="fade-up" data-aos-delay="200">
                {Features.contactPage.hero.subtitle[language]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="contact-info-section py-5">
        <div className="container">
          <div className="row mb-5">
            <div className="col-12 text-center" data-aos="fade-up">
              <h2 className="fw-bold">{Features.contactPage.contactInfo.title[language]}</h2>
              <p className="lead">{Features.contactPage.contactInfo.subtitle[language]}</p>
            </div>
          </div>
          
          <div className="row gy-4">
            <div className="col-lg-5" data-aos="fade-right" data-aos-delay="100">
              <div className={`contact-card p-4 rounded shadow-sm h-100 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-white'}`}>
                <h3 className="h4 mb-4">{Features.contactPage.contactInfo.clinic.name[language]}</h3>
                
                <div className="info-item d-flex mb-4">
                  <div className="icon me-3">
                    <i className="bi bi-geo-alt-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">{Features.contactPage.contactInfo.address.label[language]}</h5>
                    <p className="mb-2">{Features.contactPage.contactInfo.address.value[language].split('\n').map((line, i) => 
                      <React.Fragment key={i}>
                        {line}
                        {i < Features.contactPage.contactInfo.address.value[language].split('\n').length - 1 && <br />}
                      </React.Fragment>
                    )}</p>
                    <a href="https://maps.google.com/?q=Av.+Eng.+Duarte+Pacheco,+642,+4445-416+Ermesinde" 
                       className="btn btn-sm btn-outline-primary rounded-pill" 
                       target="_blank" 
                       rel="noreferrer">
                      <i className="bi bi-map me-1"></i> {Features.contactPage.contactInfo.address.mapButton[language]}
                    </a>
                  </div>
                </div>
                
                <div className="info-item d-flex mb-4">
                  <div className="icon me-3">
                    <i className="bi bi-clock-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">{Features.contactPage.contactInfo.schedule.label[language]}</h5>
                    <div className="d-flex align-items-center mb-1">
                      <span className="badge bg-primary me-2">{Features.contactPage.contactInfo.schedule.weekdays.label[language]}</span>
                      <span>{Features.contactPage.contactInfo.schedule.weekdays.hours[language]}</span>
                    </div>
                    <div className="d-flex align-items-center mb-1">
                      <span className="badge bg-info me-2">{Features.contactPage.contactInfo.schedule.saturday.label[language]}</span>
                      <span>{Features.contactPage.contactInfo.schedule.saturday.hours[language]}</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-danger me-2">{Features.contactPage.contactInfo.schedule.sunday.label[language]}</span>
                      <span>{Features.contactPage.contactInfo.schedule.sunday.status[language]}</span>
                    </div>
                  </div>
                </div>
                
                <div className="info-item d-flex mb-4">
                  <div className="icon me-3">
                    <i className="bi bi-telephone-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">{Features.contactPage.contactInfo.phone.label[language]}</h5>
                    <p className="mb-0">
                      <a href={`https://wa.me/351${Features.contactPage.contactInfo.phone.value[language].replace(/[^0-9]/g, '')}`}
                         className={`${theme === 'dark' ? 'text-light' : ''} text-decoration-none`}
                         target="_blank"
                         rel="noreferrer">
                        {Features.contactPage.contactInfo.phone.value[language]}
                      </a>
                      <span
                        className="phone-info-icon"
                        data-tooltip-text={language === 'en' ? 'Call to a national landline' : 'Chamada para a rede fixa nacional'}
                      >
                        <i className="bi bi-info-circle-fill"></i>
                      </span>
                      <br />
                      <small className={theme === 'dark' ? 'text-light opacity-75' : ''}>{Features.contactPage.contactInfo.phone.note[language]}</small>
                    </p>
                  </div>
                </div>
                
                <div className="info-item d-flex mb-4">
                  <div className="icon me-3">
                    <i className="bi bi-envelope-fill text-primary fs-4"></i>
                  </div>
                  <div>
                    <h5 className="mb-1">{Features.contactPage.contactInfo.email.label[language]}</h5>
                    <p className="mb-0">
                      <a href={`mailto:${Features.contactPage.contactInfo.email.value[language]}`} 
                         className={`${theme === 'dark' ? 'text-light' : ''} text-decoration-none`}>
                        {Features.contactPage.contactInfo.email.value[language]}
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="social-links mt-4">
                  <h5 className="mb-3">{Features.contactPage.contactInfo.socialMedia.label[language]}</h5>
                  <a href="https://www.facebook.com/clinicaguilhermegalvao" 
                     className="me-2 btn btn-outline-primary rounded-circle"
                     target="_blank"
                     rel="noreferrer">
                    <i className="bi bi-facebook"></i>
                  </a>
                  <a href="https://www.instagram.com/clinicaguilhermegalvao" 
                     className="me-2 btn btn-outline-primary rounded-circle"
                     target="_blank"
                     rel="noreferrer">
                    <i className="bi bi-instagram"></i>
                  </a>
                  <a href="https://wa.me/351927202009" 
                     className="me-2 btn btn-outline-primary rounded-circle"
                     target="_blank"
                     rel="noreferrer">
                    <i className="bi bi-whatsapp"></i>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="col-lg-7" data-aos="fade-left" data-aos-delay="200">
              <div className={`contact-form-card p-4 rounded shadow-sm ${theme === 'dark' ? 'bg-dark text-light' : 'bg-white'}`}>
                <h3 className="h4 mb-4">{Features.contactPage.contactForm.title[language]}</h3>
                
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="form-floating mb-3">
                        <input 
                          type="text" 
                          className={`form-control ${theme === 'dark' ? 'bg-secondary text-light border-secondary' : ''}`}
                          style={{ backgroundColor: theme === 'dark' ? '#2a2c2e' : '' }} 
                          id="name" 
                          name="name" 
                          placeholder={Features.contactPage.contactForm.fields.name.placeholder[language]} 
                          value={formData.name}
                          onChange={handleChange}
                          required 
                        />
                        <label htmlFor="name" className={theme === 'dark' ? 'text-light' : ''}>{Features.contactPage.contactForm.fields.name.label[language]}</label>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-floating mb-3">
                        <input 
                          type="email" 
                          className={`form-control ${theme === 'dark' ? 'bg-secondary text-light border-secondary' : ''}`}
                          style={{ backgroundColor: theme === 'dark' ? '#2a2c2e' : '' }} 
                          id="email" 
                          name="email" 
                          placeholder={Features.contactPage.contactForm.fields.email.placeholder[language]} 
                          value={formData.email}
                          onChange={handleChange}
                          required 
                        />
                        <label htmlFor="email" className={theme === 'dark' ? 'text-light' : ''}>{Features.contactPage.contactForm.fields.email.label[language]}</label>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-floating mb-3">
                        <input 
                          type="tel" 
                          className={`form-control ${theme === 'dark' ? 'bg-secondary text-light border-secondary' : ''}`}
                          style={{ backgroundColor: theme === 'dark' ? '#2a2c2e' : '' }} 
                          id="phone" 
                          name="phone" 
                          placeholder={Features.contactPage.contactForm.fields.phone.placeholder[language]} 
                          value={formData.phone}
                          onChange={handleChange}
                        />
                        <label htmlFor="phone" className={theme === 'dark' ? 'text-light' : ''}>{Features.contactPage.contactForm.fields.phone.label[language]}</label>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="form-floating mb-3">
                        <select 
                          className={`form-select ${theme === 'dark' ? 'bg-secondary text-light border-secondary' : ''}`}
                          style={{ backgroundColor: theme === 'dark' ? '#2a2c2e' : '' }} 
                          id="subject" 
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                        >
                          <option value={Features.contactPage.contactForm.fields.subject.options.appointment[language]}>{Features.contactPage.contactForm.fields.subject.options.appointment[language]}</option>
                          <option value={Features.contactPage.contactForm.fields.subject.options.information[language]}>{Features.contactPage.contactForm.fields.subject.options.information[language]}</option>
                          <option value={Features.contactPage.contactForm.fields.subject.options.other[language]}>{Features.contactPage.contactForm.fields.subject.options.other[language]}</option>
                        </select>
                        <label htmlFor="subject" className={theme === 'dark' ? 'text-light' : ''}>{Features.contactPage.contactForm.fields.subject.label[language]}</label>
                      </div>
                    </div>
                    
                    <div className="col-md-12">
                      <div className="form-floating mb-3">
                        <select 
                          className={`form-select ${theme === 'dark' ? 'bg-secondary text-light border-secondary' : ''}`}
                          style={{ backgroundColor: theme === 'dark' ? '#2a2c2e' : '' }} 
                          id="timePreference" 
                          name="timePreference"
                          value={formData.timePreference}
                          onChange={handleChange}
                        >
                          <option value="" disabled>{Features.contactPage.contactForm.fields.timePreference.placeholder[language]}</option>
                          <option value={Features.contactPage.contactForm.fields.timePreference.options.morning[language]}>{Features.contactPage.contactForm.fields.timePreference.options.morning[language]}</option>
                          <option value={Features.contactPage.contactForm.fields.timePreference.options.afternoon[language]}>{Features.contactPage.contactForm.fields.timePreference.options.afternoon[language]}</option>
                        </select>
                        <label htmlFor="timePreference" className={theme === 'dark' ? 'text-light' : ''}>{Features.contactPage.contactForm.fields.timePreference.label[language]}</label>
                      </div>
                    </div>
                    
                    <div className="col-md-12">
                      <div className="form-floating mb-3">
                        <textarea 
                          className={`form-control ${theme === 'dark' ? 'bg-secondary text-light border-secondary' : ''}`}
                          id="message" 
                          name="message" 
                          placeholder={Features.contactPage.contactForm.fields.message.placeholder[language]} 
                          style={{ height: '120px', backgroundColor: theme === 'dark' ? '#2a2c2e' : '' }}
                          value={formData.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                        <label htmlFor="message" className={theme === 'dark' ? 'text-light' : ''}>{Features.contactPage.contactForm.fields.message.label[language]}</label>
                      </div>
                    </div>
                    
                    <div className="col-12">
                      <div className="form-check mb-3">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id="acceptTerms" 
                          name="acceptTerms"
                          checked={formData.acceptTerms}
                          onChange={handleChange}
                          required
                        />
                        <label className={`form-check-label ${theme === 'dark' ? 'text-light' : ''}`} htmlFor="acceptTerms">
                          {language === 'en' ? 'I accept the ' : 'Aceito os '}
                          <a href="#" onClick={handleTermsClick} className="text-decoration-underline text-primary">
                            {language === 'en' ? 'Terms and Conditions' : 'Termos e Condições'}
                          </a>
                          {language === 'en' ? ' and the ' : ' e a '}
                          <a
                            href={ROUTES.PRIVACY}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-decoration-underline text-primary"
                          >
                            {language === 'en' ? 'Privacy Policy' : 'Política de Privacidade'}
                          </a>
                          {language === 'en'
                            ? ', and consent to the processing of my personal data *'
                            : ', e concordo com o tratamento dos meus dados pessoais *'}
                        </label>
                      </div>
                      <div className={`small ${theme === 'dark' ? 'text-light opacity-75' : 'text-muted'} mb-3`}>* Campos de preenchimento obrigatório.</div>
                    </div>
                    
                    {/* reCAPTCHA widget */}
                    <div className="col-12">
                      <div className="d-flex justify-content-center mb-3" role="group" aria-label="reCAPTCHA">
                        {!RECAPTCHA_SITE_KEY ? (
                          <p className="small text-muted mb-0">Configure REACT_APP_RECAPTCHA_SITE_KEY no .env</p>
                        ) : formData.acceptTerms ? (
                          <div ref={recaptchaRef} style={{ minHeight: 78 }} />
                        ) : (
                          <p className={`small mb-0 ${theme === 'dark' ? 'text-light opacity-75' : 'text-muted'}`}>
                            {language === 'en'
                              ? 'The anti-spam check (Google reCAPTCHA) loads once you tick the box above.'
                              : 'A verificação anti-spam (Google reCAPTCHA) é carregada ao marcar a caixa acima.'}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="text-center">
                        {loading && <div className="spinner-border text-primary mb-3" role="status"></div>}
                        {error && <div className="alert alert-danger mb-3">{error}</div>}
                        {success && <div className="alert alert-success mb-3">{Features.contactPage.contactForm.messages.success[language]}</div>}

                        <button
                          type="submit"
                          className="btn btn-primary rounded-pill px-4 py-2"
                          disabled={loading}
                        >
                          <i className="bi bi-send me-2"></i>
                          {Features.contactPage.contactForm.fields.submitButton[language]}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Map Section */}
      <section className={`map-section py-5 ${theme === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}>
        <div className="container">
          <div className="row">
            <div className="col-12" data-aos="fade-up">
              <div className="map-container rounded shadow-sm overflow-hidden">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3003.3590764860673!2d-8.56701!3d41.2119!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2465e2faa85e99%3A0x5c2a7e1d6b3a0a1d!2sAv.%20Eng.%20Duarte%20Pacheco%2C%20642%2C%204445-416%20Ermesinde!5e0!3m2!1spt-PT!2spt!4v1625584000000!5m2!1spt-PT!2spt" 
                  width="100%" 
                  height="400" 
                  style={{ border: 0 }} 
                  allowFullScreen 
                  loading="lazy"
                  title={Features.contactPage.map.title[language]}
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;